<template>
  <div
    ref="container"
    :class="b({ isLoading })"
  >
    <template v-if="$viewport.isMd">
      <h2 :class="b('title')">
        {{ $t('c-filters.title') }}
      </h2>
      <template v-if="filterGroups.length">
        <c-filter-group-checkbox
          v-for="filterGroup in filterGroups"
          :key="filterGroup.fieldname"
          :filter-group="filterGroup"
          :class="b('filter-group')"
          :is-expanded-by-default="!filterGroup.collapsed"
          @update-filter="onFilterChange"
        />
      </template>
      <div
        v-else
        :class="b('no-filters-available')"
      >
        {{ $t('c-filters.no-filters-available') }}
      </div>
    </template>
    <template v-else>
      <e-button
        :class="b('button-show-filter-modal')"
        width="full"
        size="300"
        variant="secondary"
        @click="showFilterModal = true"
      >
        {{ $t('c-filters.button-show-filter') }}
      </e-button>

      <c-modal
        v-model:is-open="showFilterModal"
        id="c-filter-modal"
        :is-closable="false"
        @close="scrollToTop"
      >
        <div :class="b('filter-modal-content')">
          <c-filter-state
            v-if="filterGroupsWithSelection.length"
            :class="b('filter-state')"
            :filter-groups="filterGroups"
            :show-reset-all="false"
            @update-filter="onFilterChange"
          />

          <h3 :class="b('modal-title')">
            {{ $t('c-filters.modal-title') }}
          </h3>

          <template v-if="filterGroups.length">
            <c-filter-group-checkbox
              v-for="filterGroup in filterGroups"
              :key="filterGroup.fieldname"
              :filter-group="filterGroup"
              :class="b('filter-group')"
              :is-expanded-by-default="!filterGroup.collapsed"
              @update-filter="onFilterChange"
            />
          </template>
          <div
            v-else
            :class="b('no-filters-available')"
          >
            {{ $t('c-filters.no-filters-available') }}
          </div>

          <e-loading-indicator
            v-if="productStore.runningRequests.apiFetchProductList"
            :class="b('loader')"
            size="medium"
            fullwidth
          />
        </div>

        <template #stickyFooter>
          <div :class="b('filter-modal-footer')">
            <e-button
              variant="secondary"
              @click="onResetFilter"
            >
              {{ $t('c-filters.modal-button-reset') }}
            </e-button>
            <e-button
              variant="primary"
              autofocus
              @click="onApplyFilter"
            >
              {{ $t('c-filters.modal-button-submit') }}
            </e-button>
          </div>
        </template>
      </c-modal>
    </template>
  </div>
</template>

<script lang="ts">
  import { PropType, Ref, defineComponent, ref } from 'vue';
  import useProductStore from '@/stores/product';
  import cFilterGroupCheckbox from '@/components/c-filter-group-checkbox.vue';
  import cFilterState from '@/components/c-filter-state.vue';
  import cModal from '@/components/c-modal.vue';
  import eButton from '@/elements/e-button.vue';
  import eLoadingIndicator from '@/elements/e-loading-indicator.vue';
  import { getFilterGroupsWithSelection, resetAllFilters } from '@/helpers/filters';
  import { FilterGroup } from '@/types/filter';

  type Setup = {
    container: Ref<HTMLDivElement | null | undefined>;
    productStore: ReturnType<typeof useProductStore>;
  };
  type Data = {
    showFilterModal: boolean;
  };

  /**
   * Renders the filters.
   */
  export default defineComponent({
    name: 'c-filters',
    components: {
      eLoadingIndicator,
      cFilterState,
      cModal,
      eButton,
      cFilterGroupCheckbox,
    },

    props: {
      /**
       * Expects filter groups.
       */
      filterGroups: {
        type: Array as PropType<FilterGroup[]>,
        required: true,
      },

      /**
       * Allows passing a loading state.
       */
      isLoading: {
        type: Boolean,
        default: false,
      },
    },
    emits: {
      updateFilter: (): boolean => true,
    },

    setup(): Setup {
      return {
        container: ref(),
        productStore: useProductStore(),
      };
    },
    data(): Data {
      return {
        showFilterModal: false,
      };
    },

    computed: {
      filterGroupsWithSelection(): FilterGroup[] {
        const { filterGroups } = this;
        const groupsWithSelection = getFilterGroupsWithSelection(filterGroups);

        return groupsWithSelection.filter((group) => group.values.some((item) => item.count > 0));
      },
    },
    // watch: {},

    // beforeCreate() {},
    // created() {},
    // beforeMount() {},
    // mounted() {},
    // beforeUpdate() {},
    // updated() {},
    // activated() {},
    // deactivated() {},
    // beforeUnmount() {},
    // unmounted() {},

    methods: {
      onFilterChange() {
        this.$emit('updateFilter');
      },

      scrollToTop(): void {
        this.container?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      },

      onApplyFilter() {
        this.showFilterModal = false;
      },

      onResetFilter() {
        resetAllFilters(this.filterGroups);
        this.showFilterModal = false;

        this.$emit('updateFilter');
      },
    },
    // render() {},
  });
</script>

<style lang="scss">
  @use '@/setup/scss/variables';
  @use '@/setup/scss/mixins';
  @use '@/setup/scss/setup/headings';

  .c-filters {
    &__title {
      @extend .headline-3;

      margin-bottom: 1rem;
    }

    &__filter-group {
      &:not(:last-child) {
        margin-bottom: 2rem;
      }
    }

    &__filter-modal-footer {
      display: grid;
      grid-template-columns: 1fr;
      gap: 1rem;

      @include mixins.media(sm) {
        grid-template-columns: 1fr 1fr;
      }
    }

    &__no-filters-available {
      font-style: italic;
    }

    &__modal-title {
      margin-bottom: 2rem;
    }
  }
</style>
